import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import {
  type IInputStepProduction,
  type IInputStepRateCost,
  InputStepType,
} from 'interfaces/IInputStep';
import { InputTaskCategory } from 'interfaces/IInputTask';
import { COST_STEP_TYPES } from 'pages/FarmsPage/Workspace/Insights/costCategories';
import { convertToHectares } from 'utils/unitConversion';

import { resolveAreaHa, resolveEffectiveRate } from './insightUtils';

export interface CostLine {
  stepType: InputStepType;
  /** Total cost in $ (positive). */
  totalCost: number;
  /** Area-weighted average cost per ha across all steps of this type. */
  costPerHa: number;
  /** Number of steps that contributed to this line. */
  stepCount: number;
}

export interface GrossMarginResult {
  /**
   * 3×3 GM values in total $ (Revenue[i][j] + TVC).
   * Index: [yieldScenario][priceScenario]  (0 = Low, 1 = Mid, 2 = High)
   */
  gm: number[][];
  /**
   * 3×3 Revenue (income) values in total $, same indexing as `gm`.
   * Index: [yieldScenario][priceScenario]  (0 = Low, 1 = Mid, 2 = High)
   */
  revenue: number[][];
  /** Sum of all plan cost steps (negative, $/total). */
  tvcTotal: number;
  /** TVC normalised to $/ha using production area as reference. */
  tvcPerHa: number;
  /** Total area derived from production steps (ha). */
  revenueArea: number;
  /** False when there are no production steps and no cost steps. */
  hasData: boolean;
  /** Per-step-type cost breakdown, sorted by InputStepType value. */
  costBreakdown: CostLine[];
  /** Number of production steps that contributed to revenue. */
  productionStepCount: number;
  /**
   * Average resolved area (ha) across all production and cost steps.
   * Falls back to zone area when no steps exist.
   */
  refArea: number;
  /**
   * Sum of each plan's own refArea — the footprint of every plan in scope.
   * Equals refArea for a single plan. Used instead of refArea when the plans
   * sit on different paddocks (a crop node), where an average is misleading.
   */
  refAreaTotal: number;
  /** True when steps resolved to different area values. */
  refAreaIsApproximate: boolean;
  /**
   * Area-weighted reference yields [Low, Mid, High] across all production
   * steps. Zero when no production data exists.
   */
  refYields: [number, number, number];
  /**
   * Yield unit label from the first production step (e.g. "t"). Defaults to
   * "t" when no production step has a unit set. Displayed as "<unit>/ha".
   */
  yieldUnit: string;
  /**
   * Area-weighted reference prices [Low, Expected, High] across all
   * production steps. Zero when no production data exists.
   */
  refPrices: [number, number, number];
}

export const computeGrossMargin = (
  input: IInput,
  categories: Set<InputTaskCategory>,
  gUnits: GUnit[]
): GrossMarginResult => {
  // Shared area tracker across all steps (production + cost).
  let allAreaSum = 0;
  let allAreaCount = 0;
  let firstArea: number | null = null;
  let refAreaIsApproximate = false;

  const trackArea = (area: number) => {
    allAreaSum += area;
    allAreaCount += 1;
    if (firstArea === null) {
      firstArea = area;
    } else if (area !== firstArea) {
      refAreaIsApproximate = true;
    }
  };

  // ── Revenue + TVC — single pass, step-level dispatch ─────────────────────
  let revenueArea = 0;
  let productionStepCount = 0;
  let yieldUnit = 't';
  const revenue: number[][] = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  const wYields = [0, 0, 0];
  const wPrices = [0, 0, 0];

  let tvcTotal = 0;
  const costByType = new Map<
    InputStepType,
    { totalCost: number; totalArea: number; stepCount: number }
  >();

  for (const task of input.tasks) {
    if (!categories.has(task.category)) continue;

    for (const step of task.steps) {
      // ── Production step → Revenue ──
      if (step.stepType === InputStepType.Production) {
        const s = step as IInputStepProduction;
        const areaHa = resolveAreaHa(step, task, input, gUnits);
        if (areaHa <= 0) continue;

        revenueArea += areaHa;
        productionStepCount += 1;
        trackArea(areaHa);
        if (productionStepCount === 1 && s.yieldUnit?.unit) {
          yieldUnit = s.yieldUnit.unit;
        }

        const yields = [s.yieldLow ?? 0, s.yield ?? 0, s.yieldHigh ?? 0];
        const prices = [s.priceLow ?? 0, s.price ?? 0, s.priceHigh ?? 0];

        for (let i = 0; i < 3; i++) {
          wYields[i] += areaHa * yields[i];
          wPrices[i] += areaHa * prices[i];
        }

        for (let yi = 0; yi < 3; yi++) {
          for (let pi = 0; pi < 3; pi++) {
            revenue[yi][pi] += areaHa * yields[yi] * prices[pi];
          }
        }
        continue;
      }

      // ── Cost step → TVC ──
      if (!COST_STEP_TYPES.has(step.stepType)) continue;

      const s = step as unknown as IInputStepRateCost;
      const cost = s.cost ?? 0;
      const effectiveRate = resolveEffectiveRate(step, task, gUnits);
      if (cost <= 0 || effectiveRate === null || effectiveRate <= 0) continue;

      const areaHa = resolveAreaHa(step, task, input, gUnits);
      if (areaHa <= 0) continue;

      const stepCost = cost * effectiveRate * areaHa;

      tvcTotal -= stepCost;
      trackArea(areaHa);

      const acc = costByType.get(step.stepType) ?? {
        totalCost: 0,
        totalArea: 0,
        stepCount: 0,
      };
      acc.totalCost += stepCost;
      acc.totalArea += areaHa;
      acc.stepCount += 1;
      costByType.set(step.stepType, acc);
    }
  }

  const refYields: [number, number, number] =
    revenueArea > 0
      ? [
          wYields[0] / revenueArea,
          wYields[1] / revenueArea,
          wYields[2] / revenueArea,
        ]
      : [0, 0, 0];

  const refPrices: [number, number, number] =
    revenueArea > 0
      ? [
          wPrices[0] / revenueArea,
          wPrices[1] / revenueArea,
          wPrices[2] / revenueArea,
        ]
      : [0, 0, 0];

  const costBreakdown: CostLine[] = Array.from(costByType.entries())
    .sort(([a], [b]) => a - b)
    .map(([stepType, { totalCost, totalArea, stepCount }]) => ({
      stepType,
      totalCost,
      costPerHa: totalArea > 0 ? totalCost / totalArea : 0,
      stepCount,
    }));

  const zoneArea = input.zone?.zoneArea ?? 0;
  const zoneAreaHa =
    zoneArea > 0
      ? (convertToHectares(zoneArea, input.zone?.areaUnit ?? null, gUnits) ??
        zoneArea)
      : 0;
  const refArea =
    allAreaCount > 0 ? allAreaSum / allAreaCount : Math.max(0, zoneAreaHa);

  // ── GM matrix ────────────────────────────────────────────────────────────
  const tvcPerHa = revenueArea > 0 ? tvcTotal / revenueArea : 0;

  const gm: number[][] = Array.from({ length: 3 }, (_, yi) =>
    Array.from({ length: 3 }, (_, pi) => revenue[yi][pi] + tvcTotal)
  );

  return {
    gm,
    revenue,
    tvcTotal,
    tvcPerHa,
    revenueArea,
    hasData: revenueArea > 0 || tvcTotal !== 0,
    costBreakdown,
    productionStepCount,
    refYields,
    yieldUnit,
    refPrices,
    refArea,
    refAreaTotal: refArea,
    refAreaIsApproximate,
  };
};

/**
 * Aggregate Gross Margin across multiple inputs — e.g. every plan linked to
 * a paddock, so the Gross Margin lens can show a cumulative figure at
 * paddock scope rather than requiring one specific plan to be open.
 *
 * ── Aggregation ───────────────────────────────────────────────────────────────
 * Calls computeGrossMargin per input and sums directly (revenue cells, TVC,
 * area, cost-breakdown totals) — same "sum whatever's in scope" approach
 * every other Live Panel/Insights compute function already uses. `gm` is
 * re-derived from the summed revenue + TVC, matching the single-input
 * formula. refYields/refPrices are re-weighted by each input's own
 * revenueArea (mirrors computeFinancialRisk.ts's cross-input weighting).
 *
 * An input whose yieldUnit differs from the first one seen is excluded from
 * the yield/price/area aggregation only (its cost data still counts) —
 * mixing incommensurable units (e.g. t vs bales across different seasons of
 * the same paddock) would make the aggregated yield axis meaningless.
 *
 * refArea is the average of each input's own refArea (not summed — these
 * inputs typically share the same physical paddock footprint across
 * seasons, so summing would misrepresent the area as larger than it is).
 * Flagged approximate when inputs' refAreas differ from each other, or when
 * any individual input's own refArea was already approximate.
 *
 * Pure function — no side effects, no hooks, no Redux.
 */
export const computeGrossMarginForInputs = (
  inputs: IInput[],
  categories: Set<InputTaskCategory>,
  gUnits: GUnit[]
): GrossMarginResult => {
  const revenue: number[][] = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  let tvcTotal = 0;
  let revenueArea = 0;
  let productionStepCount = 0;
  let yieldUnit = 't';
  let yieldUnitSet = false;
  const wYields = [0, 0, 0];
  const wPrices = [0, 0, 0];

  const costByType = new Map<
    InputStepType,
    { totalCost: number; totalArea: number; stepCount: number }
  >();

  let areaSum = 0;
  let areaCount = 0;
  let firstRefArea: number | null = null;
  let refAreaIsApproximate = false;

  for (const input of inputs) {
    const result = computeGrossMargin(input, categories, gUnits);
    if (!result.hasData) continue;

    for (let yi = 0; yi < 3; yi++) {
      for (let pi = 0; pi < 3; pi++) {
        revenue[yi][pi] += result.revenue[yi][pi];
      }
    }
    tvcTotal += result.tvcTotal;

    if (result.revenueArea > 0) {
      if (!yieldUnitSet) {
        yieldUnit = result.yieldUnit;
        yieldUnitSet = true;
      }
      if (result.yieldUnit === yieldUnit) {
        revenueArea += result.revenueArea;
        productionStepCount += result.productionStepCount;
        for (let i = 0; i < 3; i++) {
          wYields[i] += result.revenueArea * result.refYields[i];
          wPrices[i] += result.revenueArea * result.refPrices[i];
        }
      }
    }

    for (const line of result.costBreakdown) {
      const acc = costByType.get(line.stepType) ?? {
        totalCost: 0,
        totalArea: 0,
        stepCount: 0,
      };
      acc.totalCost += line.totalCost;
      // Back-calculate this input's cost-line area from its own totalCost/costPerHa —
      // same trick computeCategorySpend.ts uses to merge per-ha rates across inputs.
      acc.totalArea += line.costPerHa > 0 ? line.totalCost / line.costPerHa : 0;
      acc.stepCount += line.stepCount;
      costByType.set(line.stepType, acc);
    }

    if (result.refArea > 0) {
      areaSum += result.refArea;
      areaCount += 1;
      if (firstRefArea === null) {
        firstRefArea = result.refArea;
      } else if (result.refArea !== firstRefArea) {
        refAreaIsApproximate = true;
      }
      if (result.refAreaIsApproximate) refAreaIsApproximate = true;
    }
  }

  const refYields: [number, number, number] =
    revenueArea > 0
      ? [
          wYields[0] / revenueArea,
          wYields[1] / revenueArea,
          wYields[2] / revenueArea,
        ]
      : [0, 0, 0];

  const refPrices: [number, number, number] =
    revenueArea > 0
      ? [
          wPrices[0] / revenueArea,
          wPrices[1] / revenueArea,
          wPrices[2] / revenueArea,
        ]
      : [0, 0, 0];

  const costBreakdown: CostLine[] = Array.from(costByType.entries())
    .sort(([a], [b]) => a - b)
    .map(([stepType, { totalCost, totalArea, stepCount }]) => ({
      stepType,
      totalCost,
      costPerHa: totalArea > 0 ? totalCost / totalArea : 0,
      stepCount,
    }));

  const refArea = areaCount > 0 ? areaSum / areaCount : 0;
  const tvcPerHa = revenueArea > 0 ? tvcTotal / revenueArea : 0;

  const gm: number[][] = Array.from({ length: 3 }, (_, yi) =>
    Array.from({ length: 3 }, (_, pi) => revenue[yi][pi] + tvcTotal)
  );

  return {
    gm,
    revenue,
    tvcTotal,
    tvcPerHa,
    revenueArea,
    hasData: revenueArea > 0 || tvcTotal !== 0,
    costBreakdown,
    productionStepCount,
    refYields,
    yieldUnit,
    refPrices,
    refArea,
    refAreaTotal: areaSum,
    refAreaIsApproximate,
  };
};

/**
 * The area (ha) a GM result's dollar figures are spread over when shown as
 * $/ha — production area, falling back to the average step/zone area for a
 * plan with costs but no production step. 0 when neither is known.
 * Shared by the Margin & break-even lens's $/ha toggle and the Live totals
 * crop-average comparison (OP-2392/OP-2393) so both divide by the same area.
 */
export type GmAreaBasis = 'average' | 'total';

export const resolveGmAreaHa = (
  result: GrossMarginResult,
  areaBasis: GmAreaBasis = 'average'
): number =>
  result.revenueArea > 0
    ? result.revenueArea
    : Math.max(0, areaBasis === 'total' ? result.refAreaTotal : result.refArea);

/** The 3×3 GM matrix in $/ha, or null when there's no area to divide by. */
export const toGmPerHa = (
  result: GrossMarginResult,
  areaBasis: GmAreaBasis = 'average'
): number[][] | null => {
  const areaHa = resolveGmAreaHa(result, areaBasis);
  if (areaHa <= 0) return null;
  return result.gm.map((row) => row.map((value) => value / areaHa));
};
